<script lang="ts">
	import ScrollReveal from '$lib/components/ScrollReveal.svelte';
	import GlowCard from '$lib/components/GlowCard.svelte';
	import FloatingDashboard from '$lib/components/FloatingDashboard.svelte';
	import ApiShowcase from '$lib/components/ApiShowcase.svelte';
	import { t, type Language } from '$lib/i18n';
	import { getPlatformForSME, getPlatformForEnterprise } from '$lib/data/platform';

	interface Props {
		lang: Language;
	}

	let { lang }: Props = $props();
	let tr = $derived(t(lang));
	let platformForSME = $derived(getPlatformForSME(lang));
	let platformForEnterprise = $derived(getPlatformForEnterprise(lang));
</script>

<section id="analytics" class="py-16 md:py-32 relative overflow-hidden">
	<!-- Background effects -->
	<div class="absolute inset-0">
		<div
			class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-ameta-green/5 rounded-full blur-3xl"
		></div>
	</div>

	<div class="relative max-w-7xl mx-auto px-6">
		<ScrollReveal>
			<div class="text-center max-w-3xl mx-auto mb-8 md:mb-16">
				<div
					class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ameta-green/10 border border-ameta-green/30 mb-6"
				>
					<svg
						aria-hidden="true"
						class="w-4 h-4 text-ameta-green"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
						/>
					</svg>
					<span class="text-sm font-medium text-ameta-green">{tr('analytics_badge')}</span>
				</div>

				<h2 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
					{tr('analytics_title')}
				</h2>

				<p class="text-xl text-slate-400 leading-relaxed">
					{tr('analytics_subtitle')}
				</p>
			</div>
		</ScrollReveal>

		<div class="grid lg:grid-cols-2 gap-8 mb-8 md:mb-16">
			<!-- SME offering -->
			<ScrollReveal delay={100}>
				<GlowCard class="h-full">
					<div class="p-8">
						<div class="flex items-center gap-3 mb-6">
							<div
								class="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center"
							>
								<svg
									aria-hidden="true"
									class="w-6 h-6 text-ameta-green"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
									/>
								</svg>
							</div>
							<div>
								<h3 class="text-xl font-bold text-slate-100">{tr('analytics_full_title')}</h3>
								<p class="text-slate-500 text-sm">{tr('analytics_full_subtitle')}</p>
							</div>
						</div>
						<p class="text-slate-400 mb-6">
							{tr('analytics_full_desc')}
						</p>
						<div class="space-y-3">
							{#each platformForSME as feature}
								<div class="flex items-center gap-3">
									<div
										class="w-5 h-5 rounded-full bg-ameta-green/20 flex items-center justify-center flex-shrink-0"
									>
										<svg
											aria-hidden="true"
											class="w-3 h-3 text-ameta-green"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="3"
												d="M5 13l4 4L19 7"
											/>
										</svg>
									</div>
									<span class="text-slate-300 text-sm">{feature}</span>
								</div>
							{/each}
						</div>
					</div>
				</GlowCard>
			</ScrollReveal>

			<!-- Enterprise offering -->
			<ScrollReveal delay={200}>
				<GlowCard class="h-full">
					<div class="p-8">
						<div class="flex items-center gap-3 mb-6">
							<div
								class="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center"
							>
								<svg
									aria-hidden="true"
									class="w-6 h-6 text-ameta-green"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
									/>
								</svg>
							</div>
							<div>
								<h3 class="text-xl font-bold text-slate-100">{tr('analytics_api_title')}</h3>
								<p class="text-slate-500 text-sm">{tr('analytics_api_subtitle')}</p>
							</div>
						</div>
						<p class="text-slate-400 mb-6">
							{tr('analytics_api_desc')}
						</p>
						<div class="space-y-3">
							{#each platformForEnterprise as feature}
								<div class="flex items-center gap-3">
									<div
										class="w-5 h-5 rounded-full bg-ameta-green/20 flex items-center justify-center flex-shrink-0"
									>
										<svg
											aria-hidden="true"
											class="w-3 h-3 text-ameta-green"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="3"
												d="M5 13l4 4L19 7"
											/>
										</svg>
									</div>
									<span class="text-slate-300 text-sm">{feature}</span>
								</div>
							{/each}
						</div>
					</div>
				</GlowCard>
			</ScrollReveal>
		</div>

		<!-- Visual showcase: Dashboard + API -->
		<div class="grid lg:grid-cols-2 gap-8 items-start">
			<ScrollReveal delay={300} class="min-w-0">
				<div class="overflow-hidden min-w-0">
					<p class="text-sm text-slate-500 mb-4 text-center lg:text-left">
						{tr('analytics_dashboard_label')}
					</p>
					<svelte:boundary>
						<FloatingDashboard />
						{#snippet failed(_error, reset)}
							<div class="p-8 text-center text-slate-400">
								<p>Dashboard visualization unavailable.</p>
								<button onclick={reset} class="mt-2 text-sm underline">Retry</button>
							</div>
						{/snippet}
					</svelte:boundary>
				</div>
			</ScrollReveal>

			<ScrollReveal delay={400} class="min-w-0">
				<div class="overflow-hidden min-w-0">
					<p class="text-sm text-slate-500 mb-4 text-center lg:text-left">
						{tr('analytics_api_label')}
					</p>
					<ApiShowcase />
				</div>
			</ScrollReveal>
		</div>

		<ScrollReveal delay={500}>
			<div class="text-center mt-16">
				<a href="#contact" class="btn-primary inline-flex items-center gap-2">
					<span>{tr('analytics_cta')}</span>
					<svg
						aria-hidden="true"
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M17 8l4 4m0 0l-4 4m4-4H3"
						/>
					</svg>
				</a>
			</div>
		</ScrollReveal>
	</div>
</section>
